import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  SimpleGrid
} from "@chakra-ui/react";
import { FaExternalLinkAlt, FaArrowRight } from "react-icons/fa";
import { FiUsers, FiBookOpen, FiLayers, FiGrid, FiSettings } from "react-icons/fi";
import { IoGitCompareOutline } from "react-icons/io5";
import { useUser } from "../context/UserContext";

const HomePage: React.FC = () => {
  const { username, userId } = useUser();

  const cards = [
    {
      icon: IoGitCompareOutline,
      title: "Metric Graph",
      headline: "Connect Your Data",
      description: "Build the foundation - link your metrics, benchmarks, and business dimensions so agents understand your data structure.",
      primaryCta: "View Graph",
      primaryRoute: "/mkg",
      secondaryCta: "Connect New Data",
      secondaryRoute: "/mkg",
      color: "purple"
    },
    {
      icon: FiLayers,
      title: "Plans",
      headline: "Teach Anko Your Business Logic",
      description: "Define how your business views data - the metrics, filters, and analysis patterns that matter to your team.",
      primaryCta: "Create a Plan",
      primaryRoute: "/plan",
      secondaryCta: "Browse plans",
      secondaryRoute: "/plan",
      color: "purple"
    },
    {
      icon: FiUsers,
      title: "Agents",
      headline: "Create Custom AI Analysts",
      description: "Set up intelligent agents that will analyze your business performance and send insights to your team via Slack.",
      primaryCta: "Create an Agent",
      primaryRoute: "/agent",
      secondaryCta: "View existing agents",
      secondaryRoute: "/agent",
      color: "purple"
    },
    {
      icon: FiBookOpen,
      title: "Insights",
      headline: "Chat with Your Agents",
      description: "Review analysis, ask follow-up questions, and dive deeper into insights with your AI agents through interactive conversations.",
      primaryCta: "Start a Chat",
      primaryRoute: "/analyze",
      secondaryCta: "Recent conversations",
      secondaryRoute: "/analyze",
      color: "purple"
    },
    {
      icon: FiGrid,
      title: "Library",
      headline: "Manage Everything",
      description: "Your central hub for all agents, plans, analyses, and data connections. Keep your AI workspace organized.",
      primaryCta: "Browse Library",
      primaryRoute: "/library",
      secondaryCta: "Recent items",
      secondaryRoute: "/library",
      color: "purple"
    },
    ...(userId?.toString() === "02c73738-9481-4d0f-85c4-e927c6a09d09" ? [{
      icon: FiSettings,
      title: "Eva",
      headline: "Centralized Admin Settings",
      description: "Centralized admin settings which helps in building system prompt for AI agent configuration for old darwin.",
      primaryCta: "Access Eva",
      primaryRoute: "/old-eva",
      secondaryRoute: "/old-eva/agent/create",
      secondaryCta: "Create new Agent",
      color: "purple"
    }, {
      icon: FiSettings,
      title: "New Darwin Eva",
      headline: "Centralized Admin Settings",
      description: "Centralized admin settings which helps in building system prompt for AI agent configuration for new darwin.",
      primaryCta: "Access new darwin Eva",
      primaryRoute: "/eva",
      secondaryRoute: "/eva/agent/create",
      secondaryCta: "Create new Agent",
      color: "purple"
    }] : []),
  ];

  return (
    <Box
      width="100%"
      height="100vh"
      bg="gray.50"
      py={8}
      px={4}
      overflowY="auto"
    >
      <VStack spacing={0} maxW="7xl" mx="auto" justify="center" flex="1">
        {/* Header Section */}
        <HStack spacing={6} textAlign="left" alignSelf="flex-start" width="100%">
        <Heading size="sm" color="gray.800" fontWeight="400">
          Hello, {username || "User"} 👋
        </Heading>
      </HStack>

      <Box flex="1" display="flex" alignItems="center" width="100%">
        {/* Main Cards Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={7} width="100%" mx="auto">
          {cards.map((card, index) => (
            <Box
              key={index}
              bg="white"
              boxShadow="md"
              borderRadius="xl"
              p={6}
              borderWidth="1px"
              borderColor="gray.200"
              _hover={{
                boxShadow: "lg",
                transform: "translateY(-5px)",
                transition: "all 0.2s ease"
              }}
              transition="all 0.2s ease"
            >
              <VStack spacing={4} align="start" height="100%">
                {/* Icon and Title */}
                <HStack spacing={3}>
                <Icon as={card.icon} boxSize={7} color={`${card.color}.600`} />
                  <Heading size="xs" color="fg.default" mb={0}>
                    {card.title}
                  </Heading>
                </HStack>

                {/* Content */}
                <VStack spacing={3} align="start" flex="1">
                  <Text fontSize="lg" color="fg.default" fontWeight="500" mb={0}>
                    {card.headline}
                  </Text>
                  <Text color="gray.600" fontSize="md" lineHeight="tall" align="left">
                    {card.description}
                  </Text>
                </VStack>

                {/* CTAs */}
                <VStack spacing={2} width="100%">
                  <Button
                    as={Link}
                    to={card.primaryRoute}
                    variant="outline"
                    colorScheme={card.color}
                    bg="white"
                    border="1px solid"
                    borderColor="purple.700"
                    size="md"
                    width="100%"
                    rightIcon={<FaExternalLinkAlt size="0.75em" />}
                    fontWeight="600"
                    color="purple.700"
                    _hover={{ bg: `${card.color}.700`, color: "white" }}
                  >
                    {card.primaryCta}
                  </Button>

                  <Button
                    as={Link}
                    to={card.secondaryRoute}
                    variant="ghost"
                    size="sm"
                    width="100%"
                    rightIcon={<FaArrowRight size="0.75em" />}
                    color="gray.500"
                    _hover={{ color: `${card.color}.600`, bg: `${card.color}.50` }}
                    fontSize="xs"
                  >
                    {card.secondaryCta}
                  </Button>
                </VStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
        </Box>
      </VStack>
    </Box>
  );
};

export default HomePage;